import React, { useEffect, useRef } from 'react';
import { MUTED } from './tokens';
import SurfaceButton from './SurfaceButton';
import { useSoft, SOFT } from './SurfaceStyleContext';

/**
 * Mobile sticky price + CTA bar. Must be the LAST child of the page's scroll content (a
 * sticky child, not an absolutely-positioned sibling — as a sibling it floats over the
 * final ~68px of content). `aboveTabBar` lifts it over the global 64px mobile tab bar.
 * `sticky={false}` renders it as a plain flex-none footer for frames that own their scroller
 * (the event sheet), where it sits below the scroller instead of inside it.
 */
export const STICKY_CTA_VAR = '--pr-sticky-cta';

export default function StickyCtaBar({ price, note, cta, onCta, disabled = false, aboveTabBar = true, sticky = true, children, ctaBg, testId = 'sticky-cta-bar' }) {
  const ref = useRef(null);
  const soft = useSoft();

  // Publish the bar's height on :root while it is mounted. Fixed bottom chrome that floats
  // above the tab bar (LiveEventBanner's "your ticket" strip) reads it and moves up, so a tap
  // on GET TICKETS can never land on the banner instead.
  useEffect(() => {
    const root = document.documentElement;
    const publish = () => {
      const h = ref.current ? Math.round(ref.current.getBoundingClientRect().height) : 0;
      root.style.setProperty(STICKY_CTA_VAR, `${h}px`);
    };
    publish();
    const ro = typeof ResizeObserver !== 'undefined' && ref.current ? new ResizeObserver(publish) : null;
    ro?.observe(ref.current);
    return () => { ro?.disconnect(); root.style.removeProperty(STICKY_CTA_VAR); };
  }, []);

  // Padding lives in CSS (`.surface-cta*`, index.css): the bottom:0 variants add the
  // home-indicator inset under the buttons, which jsdom cannot parse inline.
  const position = !sticky
    ? 'flex-none surface-cta-bottom'
    : aboveTabBar ? 'sticky z-[15] surface-cta-above-tabbar' : 'sticky z-[15] bottom-0 surface-cta-bottom';
  if (soft) {
    // Soft: a black pill floating 10px off the edges — price 15px/800, note 8px faint, a 46px
    // white CTA (magenta for a Party Starter, cyan for an off-platform / held ticket via ctaBg).
    return (
      <div ref={ref} className={`surface-cta ${position}`} style={{ background: 'transparent', pointerEvents: 'none' }} data-testid={testId} data-soft="1">
        <div className="flex items-center gap-[10px] rounded-full bg-black text-white" style={{ padding: '6px 6px 6px 18px', pointerEvents: 'auto' }}>
          {(price || note) && (
            <div className="flex min-w-0 flex-1 flex-col" style={{ gap: 2, lineHeight: 1.15 }}>
              {price && <span className="font-mono" style={{ fontSize: 15, fontWeight: 800 }}>{price}</span>}
              {note && <span className="truncate font-mono font-bold uppercase" style={{ fontSize: 8, letterSpacing: '0.14em', color: SOFT.faint }}>{note}</span>}
            </div>
          )}
          {children || (
            <button
              type="button"
              onClick={onCta}
              disabled={disabled}
              className="flex-shrink-0 rounded-full font-mono uppercase text-black transition-colors duration-200 disabled:opacity-50"
              style={{ height: 46, padding: '0 20px', fontSize: 11, fontWeight: 800, letterSpacing: '0.14em', background: ctaBg || '#fff', border: '2px solid transparent' }}
            >
              {cta}
            </button>
          )}
        </div>
      </div>
    );
  }
  return (
    <div
      ref={ref}
      className={`surface-cta flex items-center gap-[10px] border-t-2 border-black bg-white ${position}`}
      data-testid={testId}
    >
      {(price || note) && (
        <div className="min-w-0" style={{ lineHeight: 1.2 }}>
          {price && <div className="font-mono font-bold" style={{ fontSize: 17 }}>{price}</div>}
          {note && <div className="truncate font-mono uppercase" style={{ fontSize: 9, letterSpacing: '0.1em', color: MUTED }}>{note}</div>}
        </div>
      )}
      {children || (
        <SurfaceButton variant="fill" size="tap" className="flex-1" onClick={onCta} disabled={disabled}>{cta}</SurfaceButton>
      )}
    </div>
  );
}
